/**
 * Placement maths for the desktop meal-config cell popover.
 *
 * Lives in a plain `.ts` (not next to the JSX) because `tsconfig` emits
 * `jsx: preserve`, so Jest cannot import anything from a `.tsx`.
 *
 * All coordinates are relative to the overlay host — the absolutely-positioned
 * View the popover renders into — not to the window.
 */

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface Placement {
  left: number;
  top: number;
}

/** Panel width, shared with the stylesheet so the clamp stays honest. */
export const POPOVER_WIDTH = 248;

/**
 * First-paint estimate of the panel height (header + stepper + reset link +
 * diet grid + remove). The panel measures itself on layout and re-places with
 * the real value, so this only has to be close; erring high just flips it above
 * the anchor slightly earlier than needed.
 */
export const POPOVER_HEIGHT = 344;

/** Gap between the anchor cell and the panel. */
const GAP = 6;

/** Minimum breathing room between the panel and the container edges. */
const MARGIN = 8;

/**
 * Right-align the panel to its anchor and drop it below, flipping above when it
 * would run off the bottom and clamping so it never leaves the container.
 */
export function placeCellPopover(
  anchor: Rect,
  container: { width: number; height: number },
  panel: { width: number; height: number } = { width: POPOVER_WIDTH, height: POPOVER_HEIGHT },
  /**
   * Height at the bottom of the container that is covered by something floating
   * over it (the action bar). Inside the container is not the same as visible.
   */
  bottomInset: number = 0
): Placement {
  const maxLeft = container.width - panel.width - MARGIN;
  const left = Math.max(MARGIN, Math.min(anchor.x + anchor.width - panel.width, maxLeft));

  const usableHeight = container.height - bottomInset;
  const below = anchor.y + anchor.height + GAP;
  const above = anchor.y - panel.height - GAP;
  const fitsBelow = below + panel.height <= usableHeight - MARGIN;
  const top = Math.max(MARGIN, fitsBelow ? below : above);

  return { left, top };
}
